/**
 * OAuth Callback Error Page
 *
 * Shown when OAuth login fails. Displays error and provides retry option.
 */

import * as React from 'react';
import { createFileRoute, Link, useSearch } from '@tanstack/react-router';
import { XCircle, ArrowLeft } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/auth/callback/error')({
  component: OAuthErrorPage,
  validateSearch: (search: Record<string, unknown>): { message?: string } => {
    return {
      message: typeof search.message === 'string' ? search.message : undefined,
    };
  },
});

function OAuthErrorPage(): React.JSX.Element {
  const { message } = useSearch({ from: '/auth/callback/error' });

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
            <XCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
          </div>
          <CardTitle>Authentication Failed</CardTitle>
          <CardDescription>
            We couldn't complete your sign-in request.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {message && (
            <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
              {message}
            </div>
          )}
          {!message && (
            <p className="text-sm text-muted-foreground">
              An unexpected error occurred during authentication. Please try again.
            </p>
          )}
        </CardContent>
        <CardFooter className="flex flex-col gap-3">
          <Link to="/login" className="w-full">
            <Button variant="default" className="w-full">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Login
            </Button>
          </Link>
          <p className="text-xs text-muted-foreground">
            If this problem persists, please{' '}
            <Link to="/contact" search={{ type: undefined }} className="text-primary hover:underline">
              contact support
            </Link>
            .
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
